import React, { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogBackdrop } from '@headlessui/react'
import { useDispatch } from 'react-redux'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Button from '@atlaskit/button'
import TextField from '@atlaskit/textfield'
import Toggle from '@atlaskit/toggle'
import DragHandlerIcon from '@atlaskit/icon/glyph/drag-handler'
import TrashIcon from '@atlaskit/icon/glyph/trash'
import AddIcon from '@atlaskit/icon/glyph/add'
import InfoIcon from '@atlaskit/icon/glyph/info'
import EditorEditIcon from '@atlaskit/icon/glyph/editor/edit'
import EditorDoneIcon from '@atlaskit/icon/glyph/editor/done'
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close'
import CrossIcon from '@atlaskit/icon/glyph/cross'
import Tooltip from '@atlaskit/tooltip'
import Spinner from '@atlaskit/spinner'
import { token } from '@atlaskit/tokens'
import { useNotification } from '../../contexts/NotificationContext'
import { ConfirmDeleteModal } from '../FolderBrowser/ConfirmDeleteModal'
import {
  createCustomFieldAsync,
  deleteCustomFieldAsync,
  bulkUpdateCustomFieldsAsync,
  updateCustomFieldAsync,
  fetchTestRunCustomFieldsAsync
} from '../../store/slices/testScenarioSlice'

/**
 * SORTABLE FIELD ROW
 *
 * Individual draggable row for a custom field.
 * Contains drag handle, field name (with inline rename for non-default fields),
 * visibility toggle, and delete button.
 *
 * Non-default fields show a pencil icon on hover. Clicking it enters inline
 * edit mode where the user can rename the field. The rename is saved immediately
 * via API (not batched with reorder/visibility changes).
 */
function SortableFieldRow({ field, onToggleVisibility, onDelete, onRename, showError }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: field.id })

  // Inline rename state
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(field.fieldName)
  const [isSaving, setIsSaving] = useState(false)
  const [validationError, setValidationError] = useState('')

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  /**
   * ENTER EDIT MODE
   * Sets editing state and pre-fills with current field name
   */
  const handleStartEdit = () => {
    setEditValue(field.fieldName)
    setValidationError('')
    setIsEditing(true)
  }

  /**
   * CANCEL EDIT
   * Reverts to original field name and exits edit mode
   */
  const handleCancelEdit = () => {
    setEditValue(field.fieldName)
    setValidationError('')
    setIsEditing(false)
  }

  /**
   * SAVE RENAMED FIELD
   * Validates non-empty, calls onRename API handler, then exits edit mode
   */
  const handleSaveEdit = async () => {
    const trimmed = editValue.trim()

    // Validate non-empty
    if (!trimmed) {
      setValidationError('Field name is required')
      return
    }

    // Skip if name hasn't changed
    if (trimmed === field.fieldName) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    setValidationError('')

    try {
      await onRename(field.id, trimmed)
      setIsEditing(false)
    } catch (error) {
      showError('Rename Failed', error.message || 'Failed to rename field')
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * HANDLE KEY DOWN IN EDIT MODE
   * Enter = save, Escape = cancel
   */
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSaveEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancelEdit()
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="custom-field-row"
    >
      {/* Drag Handle */}
      <span
        className="field-drag-handle"
        {...listeners}
        {...attributes}
        style={{
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          padding: '4px',
          color: token('color.icon.subtle')
        }}
      >
        <DragHandlerIcon label="Drag to reorder" size="small" />
      </span>

      {/* Field Name - inline editable for non-default fields */}
      {isEditing ? (
        /* EDIT MODE: Inline TextField with confirm/cancel buttons */
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div style={{ flex: 1 }}>
            <TextField
              value={editValue}
              onChange={(e) => {
                setEditValue(e.target.value)
                if (validationError) setValidationError('')
              }}
              onKeyDown={handleEditKeyDown}
              autoFocus
              isCompact
              isDisabled={isSaving}
              isInvalid={!!validationError}
              aria-label="Field name"
            />
            {/* Validation error message */}
            {validationError && (
              <div
                style={{
                  fontSize: '11px',
                  color: token('color.text.danger', '#DE350B'),
                  marginTop: '2px'
                }}
              >
                {validationError}
              </div>
            )}
          </div>
          {/* Confirm/Cancel buttons or spinner */}
          {isSaving ? (
            <Spinner size="small" />
          ) : (
            <>
              <Button
                appearance="subtle"
                spacing="compact"
                iconBefore={<EditorDoneIcon label="Confirm" size="small" primaryColor={token('color.icon.success', '#22A06B')} />}
                onClick={handleSaveEdit}
                title="Save name (Enter)"
              />
              <Button
                appearance="subtle"
                spacing="compact"
                iconBefore={<EditorCloseIcon label="Cancel" size="small" />}
                onClick={handleCancelEdit}
                title="Cancel (Escape)"
              />
            </>
          )}
        </div>
      ) : (
        /* READ MODE: Field name with pencil icon on hover for non-default fields */
        <span
          className="field-name"
          style={{
            flex: 1,
            fontSize: '14px',
            color: token('color.text'),
            fontWeight: field.isDefault ? 500 : 400,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          {field.fieldName}
          {field.isDefault && (
            <span
              style={{
                marginLeft: '4px',
                fontSize: '11px',
                color: token('color.text.subtle'),
                fontStyle: 'italic'
              }}
            >
              (default)
            </span>
          )}
          {/* Pencil icon - only for non-default fields, visible on row hover */}
          {!field.isDefault && (
            <span className="field-edit-icon">
              <Button
                appearance="subtle"
                spacing="compact"
                iconBefore={<EditorEditIcon label="Rename field" size="small" />}
                onClick={handleStartEdit}
                title="Rename field"
              />
            </span>
          )}
        </span>
      )}

      {/* Field Type Badge */}
      <span
        style={{
          fontSize: '11px',
          padding: '2px 6px',
          borderRadius: '3px',
          backgroundColor: token('color.background.neutral'),
          color: token('color.text.subtle'),
          marginRight: '12px'
        }}
      >
        {field.fieldType}
      </span>

      {/* Visibility Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px' }}>
        <span style={{ fontSize: '12px', color: token('color.text.subtle') }}>
          {field.showField ? 'Visible' : 'Hidden'}
        </span>
        <Toggle
          isChecked={field.showField}
          onChange={() => onToggleVisibility(field.id)}
          size="regular"
        />
      </div>

      {/* Delete Button */}
      <Button
        appearance="subtle"
        spacing="compact"
        iconBefore={<TrashIcon label="Delete" size="small" />}
        isDisabled={field.isDefault}
        onClick={() => onDelete(field)}
        title={field.isDefault ? 'Default fields cannot be deleted' : 'Delete field'}
        style={{
          opacity: field.isDefault ? 0.4 : 1
        }}
      />
    </div>
  )
}

/**
 * CUSTOM FIELDS MODAL
 *
 * Modal for managing test run custom fields (execution columns).
 * Allows users to:
 * - View all custom fields (visible and hidden)
 * - Reorder fields via drag-and-drop
 * - Toggle field visibility
 * - Delete non-default fields
 * - Add new TEXT fields
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Callback to close modal
 * @param {Array} customFields - Array of custom field definitions from Redux
 * @param {number} jiraProjectId - Jira project ID for API calls
 */
export function CustomFieldsModal({ isOpen, onClose, customFields, jiraProjectId }) {
  const dispatch = useDispatch()
  const { showError } = useNotification()

  // Local state for field list (working copy)
  const [localFields, setLocalFields] = useState([])
  // New field name input
  const [newFieldName, setNewFieldName] = useState('')
  // Form states
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isAddingField, setIsAddingField] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  // Delete confirmation
  const [fieldToDelete, setFieldToDelete] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5 // 5px threshold before drag activates
      }
    })
  )

  // Initialize local fields when modal opens or customFields change
  useEffect(() => {
    if (isOpen && customFields) {
      // Create a sorted copy of fields
      const sortedFields = [...customFields].sort((a, b) => a.orderIndex - b.orderIndex)
      setLocalFields(sortedFields)
      setHasChanges(false)
      setNewFieldName('')
      setFieldToDelete(null)
      setIsDeleteModalOpen(false)
    }
  }, [isOpen, customFields])

  // Get sortable IDs for DndContext
  const sortableIds = useMemo(() => localFields.map(f => f.id), [localFields])

  /**
   * HANDLE DRAG END
   *
   * Reorders the local fields array when a field is dropped
   */
  const handleDragEnd = (event) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = localFields.findIndex(f => f.id === active.id)
    const newIndex = localFields.findIndex(f => f.id === over.id)

    if (oldIndex !== -1 && newIndex !== -1) {
      const reordered = arrayMove(localFields, oldIndex, newIndex)
      setLocalFields(reordered)
      setHasChanges(true)
    }
  }

  /**
   * HANDLE TOGGLE VISIBILITY
   *
   * Toggles the showField property for a field
   */
  const handleToggleVisibility = (fieldId) => {
    setLocalFields(prev =>
      prev.map(field =>
        field.id === fieldId
          ? { ...field, showField: !field.showField }
          : field
      )
    )
    setHasChanges(true)
  }

  /**
   * HANDLE RENAME FIELD
   *
   * Renames a non-default field immediately via API.
   * This is a discrete action, not batched with reorder/visibility saves.
   */
  const handleRenameField = async (fieldId, newName) => {
    await dispatch(updateCustomFieldAsync({
      fieldId,
      jiraProjectId,
      updates: { fieldName: newName }
    })).unwrap()

    // Update local state to reflect the rename
    setLocalFields(prev =>
      prev.map(f => f.id === fieldId ? { ...f, fieldName: newName } : f)
    )
  }

  /**
   * HANDLE DELETE CLICK
   *
   * Opens delete confirmation for a field
   */
  const handleDeleteClick = (field) => {
    setFieldToDelete(field)
    setIsDeleteModalOpen(true)
  }

  /**
   * CONFIRM DELETE
   *
   * Deletes the field via API and removes from local state
   */
  const confirmDelete = async () => {
    if (!fieldToDelete) return

    await dispatch(deleteCustomFieldAsync({
      fieldId: fieldToDelete.id,
      jiraProjectId
    })).unwrap()

    // Remove from local state
    setLocalFields(prev => prev.filter(f => f.id !== fieldToDelete.id))
    setFieldToDelete(null)
  }

  /**
   * HANDLE ADD FIELD
   *
   * Creates a new custom field via API
   */
  const handleAddField = async () => {
    if (!newFieldName.trim()) return

    setIsAddingField(true)

    try {
      await dispatch(createCustomFieldAsync({
        fieldName: newFieldName.trim(),
        jiraProjectId
      })).unwrap()

      // Clear input - localFields will update via useEffect when Redux state changes
      setNewFieldName('')
    } catch (error) {
      showError('Create Failed', error.message || 'Failed to create field')
    } finally {
      setIsAddingField(false)
    }
  }

  /**
   * HANDLE SAVE
   *
   * Saves all changes (reorder and visibility) via bulk update API
   */
  const handleSave = async () => {
    if (!hasChanges) {
      onClose()
      return
    }

    setIsSubmitting(true)

    try {
      // Collect fields that have changed (orderIndex or showField)
      const updates = localFields
        .map((field, index) => {
          const originalField = customFields.find(f => f.id === field.id)
          const newOrderIndex = index + 1

          // Check if anything changed
          const orderChanged = originalField?.orderIndex !== newOrderIndex
          const visibilityChanged = originalField?.showField !== field.showField

          if (orderChanged || visibilityChanged) {
            return {
              fieldId: field.id,
              orderIndex: newOrderIndex,
              showField: field.showField
            }
          }
          return null
        })
        .filter(Boolean)

      if (updates.length > 0) {
        await dispatch(bulkUpdateCustomFieldsAsync({
          jiraProjectId,
          updates
        })).unwrap()

        // Refresh the custom fields from server to ensure sync
        await dispatch(fetchTestRunCustomFieldsAsync(jiraProjectId))
      }

      onClose()
    } catch (error) {
      showError('Save Failed', error.message || 'Failed to save changes')
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * HANDLE CANCEL
   *
   * Closes modal without saving changes
   */
  const handleCancel = () => {
    if (hasChanges) {
      // Could add a confirmation dialog here if needed
    }
    onClose()
  }

  /**
   * HANDLE KEY DOWN ON NEW FIELD INPUT
   *
   * Submits on Enter key
   */
  const handleNewFieldKeyDown = (e) => {
    if (e.key === 'Enter' && newFieldName.trim() && !isAddingField) {
      handleAddField()
    }
  }

  return (
    <>
    <Dialog open={isOpen} onClose={handleCancel} className="headless-dialog-root">
      <DialogBackdrop className="headless-dialog-overlay" />

      <div className="headless-dialog-panel-wrapper">
        <Dialog.Panel
          className="headless-dialog-panel"
          style={{
            width: '540px',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* HEADER */}
          <div
            className="headless-dialog-header"
            style={{
              padding: '16px 24px',
              borderBottom: `1px solid ${token('color.border')}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0
            }}
          >
            {/* Left: Title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
              <Dialog.Title
                className="headless-dialog-title"
                style={{ margin: 0 }}
              >
                Manage Execution Fields
              </Dialog.Title>
            </div>

            {/* Right: Close button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <Tooltip content="Close">
                <Button
                  appearance="subtle"
                  iconBefore={<CrossIcon label="Close" size="medium" />}
                  onClick={onClose}
                />
              </Tooltip>
            </div>
          </div>

          {/* BODY */}
          <div
            className="headless-dialog-body"
            style={{
              padding: '16px 20px',
              flex: 1,
              overflow: 'auto'
            }}
          >
            {/* Project-level notice - informs user that changes affect all scenarios */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '12px 16px',
                marginBottom: '16px',
                backgroundColor: token('color.background.information', '#DEEBFF'),
                borderRadius: '3px'
              }}
            >
              <span style={{ color: token('color.icon.information', '#0052CC'), flexShrink: 0, marginTop: '2px' }}>
                <InfoIcon label="Information" size="medium" />
              </span>
              <p style={{ margin: 0, fontSize: '14px', color: token('color.text', '#172B4D'), lineHeight: '20px' }}>
                Changes to execution fields apply to <strong>all test scenarios</strong> in this project, not just the current one.
              </p>
            </div>

            {/* Field List with Drag and Drop */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortableIds}
                strategy={verticalListSortingStrategy}
              >
                <div className="custom-fields-list">
                  {localFields.length === 0 ? (
                    <p style={{ color: token('color.text.subtle'), fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                      No custom fields available.
                    </p>
                  ) : (
                    localFields.map(field => (
                      <SortableFieldRow
                        key={field.id}
                        field={field}
                        onToggleVisibility={handleToggleVisibility}
                        onDelete={handleDeleteClick}
                        onRename={handleRenameField}
                        showError={showError}
                      />
                    ))
                  )}
                </div>
              </SortableContext>
            </DndContext>

            {/* Add New Field Section */}
            <div
              style={{
                marginTop: '20px',
                paddingTop: '16px',
                borderTop: `1px solid ${token('color.border')}`
              }}
            >
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  fontSize: '12px',
                  color: token('color.text.subtle')
                }}
              >
                Add new field
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                <div style={{ flex: 1 }}>
                  <TextField
                    value={newFieldName}
                    onChange={(e) => setNewFieldName(e.target.value)}
                    onKeyDown={handleNewFieldKeyDown}
                    placeholder="Enter field name"
                    isDisabled={isAddingField}
                    width="100%"
                  />
                </div>
                <Button
                  appearance="default"
                  iconBefore={<AddIcon label="Add" size="small" />}
                  onClick={handleAddField}
                  isLoading={isAddingField}
                  isDisabled={!newFieldName.trim() || isAddingField}
                  style={{ marginTop: '2px' }}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div
            className="headless-dialog-footer"
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              padding: '16px 20px',
              borderTop: `1px solid ${token('color.border')}`
            }}
          >
            <Button
              appearance="subtle"
              onClick={handleCancel}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSave}
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
            >
              {hasChanges ? 'Save Changes' : 'Done'}
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>

      {/* Delete Confirmation Modal - Jira-style dialog */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setFieldToDelete(null)
        }}
        onConfirm={confirmDelete}
        title="Delete Custom Field"
        itemName={fieldToDelete?.fieldName || 'custom field'}
        itemType="field"
        warningMessage="This will permanently delete this custom field and all its data from every test case. This action cannot be undone."
      />
    </>
  )
}
